import { SPECIAL_COLORS } from '../stores/styles/BuildScenarioStore.styles'

/**
 * Dummy Scenario Report API
 *
 * This utility function simulates the scenario report API response by combining
 * test payload data with scenario information from the build scenario snapshot.
 *
 * @param {Object} testPayloadData - The data from testPayload.json
 * @param {Object} snapshot - The build scenario store snapshot
 * @returns {Object} Combined data object for the scenario report
 */
const ScenarioReportPayload = (snapshot) => {
  let base_scenario = {}
  // Get original_model_end_value from initialCleanModelOutput (scenario with scenarioId 0)
  let originalModelEndValue = null
  if (
    snapshot?.initialCleanModelOutput?.scenarios &&
    Array.isArray(snapshot.initialCleanModelOutput.scenarios)
  ) {
    const initialScenario = snapshot.initialCleanModelOutput.scenarios.find(
      (s) => s.scenarioId === 0,
    )
    if (
      initialScenario?.observationValues &&
      initialScenario.observationValues.length > 0
    ) {
      originalModelEndValue =
        initialScenario.observationValues[
          initialScenario.observationValues.length - 1
        ]
      base_scenario = {
        id: initialScenario.scenarioId,
        name: 'Model prediction',
        color: SPECIAL_COLORS.CLEAN_BASELINE, // Use ice blue for original model prediction
        x: initialScenario.observationTimecodes,
        y: initialScenario.observationValues,
        predicted_model_end_value:
          initialScenario.observationValues[
            initialScenario.observationValues.length - 1
          ],
        drivers: snapshot?.independentDriversOriginalObservations.map(
          (driver) => {
            const driverMetadata = snapshot?.drivers?.find(
              (d) => d.id === driver.id,
            )
            return {
              driver_id: driver.id,
              name: driverMetadata?.name || `Driver ${driver.id}`,
              source: driverMetadata?.source || 'Unknown',
              units: driverMetadata?.units || 'Unknown',
              original_end_value: originalModelEndValue,
              mutated_end_value: originalModelEndValue,
              has_been_adjusted: false,
            }
          },
        ),
      }
    }
  }

  // Build scenarios array
  const scenarios = []

  if (snapshot?.scenarios) {
    snapshot.scenarios.forEach((scenario) => {
      // Get predicted_model_end_value from lastRemoteModelOutput
      let predictedModelEndValue = null
      let remoteScenario
      if (
        snapshot?.lastRemoteModelOutput?.scenarios &&
        Array.isArray(snapshot.lastRemoteModelOutput.scenarios)
      ) {
        remoteScenario = snapshot.lastRemoteModelOutput.scenarios.find(
          (s) => s.scenarioId === scenario.id,
        )
        if (
          remoteScenario?.observationValues &&
          remoteScenario.observationValues.length > 0
        ) {
          predictedModelEndValue =
            remoteScenario.observationValues[
              remoteScenario.observationValues.length - 1
            ]
        }
      }

      // Build drivers array
      const drivers = []

      if (scenario.driverValues) {
        scenario.driverValues.forEach((driver) => {
          // Get driver metadata from snapshot.drivers
          const driverMetadata = snapshot?.drivers?.find(
            (d) => d.id === driver.id,
          )

          // Get original_end_value from independentDriversOriginalObservations
          let originalEndValue = null
          if (snapshot?.independentDriversOriginalObservations) {
            const originalDriver =
              snapshot.independentDriversOriginalObservations.find(
                (obs) => obs.driverId === driver.id,
              )
            if (
              originalDriver?.observationValues &&
              originalDriver.observationValues.length > 0
            ) {
              originalEndValue =
                originalDriver.observationValues[
                  originalDriver.observationValues.length - 1
                ]
            }
          }

          // Get mutated_end_value from the driver's mutatedObservations
          let mutatedEndValue = null
          if (
            driver.mutatedObservations?.observationValues &&
            driver.mutatedObservations.observationValues.length > 0
          ) {
            mutatedEndValue =
              driver.mutatedObservations.observationValues[
                driver.mutatedObservations.observationValues.length - 1
              ]
          }

          drivers.push({
            driver_id: driver.id,
            name: driverMetadata?.name || `Driver ${driver.id}`,
            source: driverMetadata?.source || 'Unknown',
            units: driverMetadata?.units || 'Unknown',
            original_end_value: originalEndValue,
            mutated_end_value: mutatedEndValue,
            has_been_adjusted: originalEndValue !== mutatedEndValue,
          })
        })
      }

      scenarios.push({
        id: scenario.id,
        name: scenario.name,
        color: scenario.color, // Include scenario color
        x: remoteScenario.observationTimecodes,
        y: remoteScenario.observationValues,
        predicted_model_end_value: predictedModelEndValue,
        drivers: drivers,
      })
    })
  }

  // Create the final scenario object
  const scenarioObject = {
    original_model_end_value: originalModelEndValue,
    base_scenario,
    scenarios: scenarios,
  }

  return scenarioObject
}

export default ScenarioReportPayload
